import { useMutation, useQuery } from '@apollo/client';
import { Col, Row, Skeleton } from 'antd';
import Text from 'antd/lib/typography/Text';
import { useNavigate } from 'react-router-dom';
import { CustomNotification } from 'src/components/custom-notification';
import { ROUTES } from 'src/config/route';
import { CreateSlider, UpdateSlider } from 'src/lib/gql/mutations';
import { QueryDefaultSlider } from 'src/lib/gql/queries';
import SliderCard from '../../components/ui/sliders/slider-card';
import {
	CmsCreateSliderMutationMutation,
	CmsCreateSliderMutationMutationVariables,
	CmsGetDefaultSliderQuery,
	CmsUpdateSliderMutationMutation,
	CmsUpdateSliderMutationMutationVariables,
	MediaType,
} from 'src/lib/generated/_generated';

const DefaultSlider = () => {
	const navigate = useNavigate();

	const {
		data: qryData,
		loading: qryLoading,
		refetch: refetchQuery,
	} = useQuery<CmsGetDefaultSliderQuery>(QueryDefaultSlider, {
		fetchPolicy: 'cache-and-network',
	});

	const [createSlider] = useMutation<
		CmsCreateSliderMutationMutation,
		CmsCreateSliderMutationMutationVariables
	>(CreateSlider, {
		onError(error) {
			CustomNotification({
				pageName: 'Sliders',
				pagePrefix: 'Slider',
				notificationType: 'custom-error',
				customDescription: error.message,
			});
		},
	});

	const [updateSlider] = useMutation<
		CmsUpdateSliderMutationMutation,
		CmsUpdateSliderMutationMutationVariables
	>(UpdateSlider, {
		onError(error) {
			CustomNotification({
				pageName: 'Sliders',
				pagePrefix: 'Slider',
				notificationType: 'custom-error',
				customDescription: error.message,
			});
		},
	});

	const existingSlider = qryData?.getDefaultSlider;

	return (
		<Row>
			<Col span={24}>
				<Text className="sub-header">Default Slide</Text>
			</Col>

			{qryLoading ? (
				<Skeleton active />
			) : existingSlider ? (
				<SliderCard
					inclSliderPosition={false}
					hideTagsAndDates={true}
					sliderName={existingSlider.title || ''}
					sliderSubtitle={existingSlider.subtitle || ''}
					sliderButtonText={existingSlider.buttonText || ''}
					sliderPosition={existingSlider.weight || undefined}
					sliderLink={existingSlider.link || ''}
					sliderMedia={
						existingSlider.media
							? {
									id: existingSlider.media.id,
									url: existingSlider.media.url,
									type: existingSlider.media.type || MediaType.Image,
									...(existingSlider.media.thumbnail && {
										thumbnail: {
											id: existingSlider.media.thumbnail.id || '-1',
											url: existingSlider.media.thumbnail.url || '',
										},
									}),
							  }
							: undefined
					}
					sliderMobileMedia={
						existingSlider.mobileMedia
							? {
									id: existingSlider.mobileMedia.id,
									url: existingSlider.mobileMedia.url,
									type: MediaType.Image,
							  }
							: undefined
					}
					onSave={async (data) => {
						const rsp = await updateSlider({
							variables: {
								id: existingSlider.id,
								mediaId: data.mediaId,
								mediaType: data.mediaType,
								title: data.name || '',
								subtitle: data.subtitle || '',
								buttonText: data.buttonText || 'Find out more',
								link: data.link,
								weight: data.position,
								videoLink: data.videoLink,
								mobileMediaId: data.mobileMediaId,
								sliderDates: [],
								sliderHTags: [],
							},
						});
						if (rsp.data) {
							CustomNotification({
								pageName: 'Sliders',
								pagePrefix: 'Default Slide',
								notificationType: 'updated',
							});
							refetchQuery();
						}
					}}
				/>
			) : (
				<SliderCard
					hideTagsAndDates={true}
					onSave={async (data) => {
						const rsp = await createSlider({
							variables: {
								mediaId: data.mediaId,
								mediaType: data.mediaType,
								title: data.name || '',
								subtitle: data.subtitle || '',
								buttonText: data.buttonText || 'Find out more',
								link: data.link,
								weight: data.position,
								videoLink: data.videoLink,
								mobileMediaId: data.mobileMediaId,
								sliderDates: [],
								sliderHTags: [],
								isDefault: true,
							},
						});
						if (rsp.data) {
							CustomNotification({
								pageName: 'Sliders',
								pagePrefix: 'Default Slide',
								notificationType: 'created',
							});
							navigate(ROUTES.DEFAULT_SLIDER.path);
							refetchQuery();
						}
					}}
				/>
			)}
		</Row>
	);
};

export default DefaultSlider;
